import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { CleanContentful } from '@stadionhq-v3/clean-contentful';
import type { CometTeam } from '@graphql/index';
import {
  COMET_PROXY_BASE,
  getRefereeAssignments,
  getRefereeTeams,
} from '@lib/api/refereeAssignments';

/** Cards shown per page (brief: 12, load-more paginates). */
export const PAGE_SIZE = 12;

/** Crest lookup runs server-side (Apollo is server-only) — see the route handler. */
const CRESTS_ENDPOINT = '/api/referee-appointments/crests';

const fetchCometTeamCrests = async (
  teamIds: string[]
): Promise<CleanContentful<CometTeam>[]> => {
  if (!teamIds.length) {
    return [];
  }
  const res = await fetch(
    `${CRESTS_ENDPOINT}?ids=${encodeURIComponent(teamIds.join(','))}`
  );
  if (!res.ok) {
    return [];
  }
  const data = (await res.json()) as {
    teams?: CleanContentful<CometTeam>[];
  };
  return data.teams ?? [];
};

/** Applied search criteria — set when the user clicks Search. */
export type RefereeSearchCriteria = {
  competitionId?: string;
  teamId?: string;
  from?: string;
  to?: string;
};

export const refereeQueryKeys = {
  teams: (competitionId?: string) =>
    ['referee-assignments', 'teams', competitionId ?? null] as const,
  assignments: (criteria: RefereeSearchCriteria | null) =>
    ['referee-assignments', 'search', criteria] as const,
  crests: (teamIds: string[]) =>
    ['referee-assignments', 'crests', [...teamIds].sort()] as const,
};

/** Teams for the selected competition (dependent query — off until one is picked). */
export const useRefereeTeams = (competitionId?: string) =>
  useQuery({
    queryKey: refereeQueryKeys.teams(competitionId),
    queryFn: () =>
      getRefereeTeams({ competitionId, baseUrl: COMET_PROXY_BASE }),
    enabled: Boolean(competitionId),
    staleTime: 5 * 60 * 1000,
  });

/**
 * Paginated search results. Disabled until `criteria` is set (Search clicked).
 * Uses `hasNextPage` from the API envelope to drive the load-more button.
 */
export const useRefereeAssignments = (criteria: RefereeSearchCriteria | null) =>
  useInfiniteQuery({
    queryKey: refereeQueryKeys.assignments(criteria),
    queryFn: ({ pageParam }) =>
      getRefereeAssignments({
        baseUrl: COMET_PROXY_BASE,
        params: { ...criteria, page: pageParam, pageSize: PAGE_SIZE },
      }),
    enabled: Boolean(criteria),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage?.hasNextPage ? lastPage.page + 1 : undefined,
    staleTime: 30 * 1000,
  });

/** Contentful cometTeam crest lookup for the currently-loaded team ids. */
export const useCometTeamCrests = (teamIds: string[]) =>
  useQuery({
    queryKey: refereeQueryKeys.crests(teamIds),
    queryFn: () => fetchCometTeamCrests(teamIds),
    enabled: teamIds.length > 0,
    staleTime: 10 * 60 * 1000,
  });
